export const SyncStatus = {
	Queued: 0,
	Processing: 1,
	Synced: 2,
	ErrorRetry: 3,
	ErrorFatal: 4,
	Skipped: 5,
} as const;

export type TSyncStatus = {
	id: number;
	label: string;
	className: string;
};

const SYNC_STATUSES: Record<number, TSyncStatus> = {
	[SyncStatus.Queued]: { id: SyncStatus.Queued, label: "Queued", className: "syncQueued" },
	[SyncStatus.Processing]: { id: SyncStatus.Processing, label: "Syncing", className: "syncProcessing" },
	[SyncStatus.Synced]: { id: SyncStatus.Synced, label: "Synced", className: "syncSynced" },
	[SyncStatus.ErrorRetry]: {
		id: SyncStatus.ErrorRetry,
		label: "Sync error (will retry)",
		className: "syncErrorRetry",
	},
	[SyncStatus.ErrorFatal]: {
		id: SyncStatus.ErrorFatal,
		label: "Sync failed (needs review)",
		className: "syncErrorFatal",
	},
	[SyncStatus.Skipped]: { id: SyncStatus.Skipped, label: "Skipped", className: "syncSkipped" },
};

export const getSyncStatus = (value: number | string | null | undefined): TSyncStatus | null => {
	if (value === null || value === undefined || String(value).trim() === "") return null;
	const id = Number(value);
	if (!Number.isFinite(id)) return null;
	return SYNC_STATUSES[id] ?? null;
};

const COLLAPSE_ORDER: ReadonlyArray<number> = [
	SyncStatus.Skipped,
	SyncStatus.Synced,
	SyncStatus.Processing,
	SyncStatus.Queued,
	SyncStatus.ErrorRetry,
	SyncStatus.ErrorFatal,
];

export const collapseSyncStatus = (values: Array<number | string | null | undefined>): number | null => {
	let topRank = -1;
	for (const value of values) {
		const status = getSyncStatus(value);
		if (!status) continue;
		topRank = Math.max(topRank, COLLAPSE_ORDER.indexOf(status.id));
	}
	return topRank < 0 ? null : COLLAPSE_ORDER[topRank];
};

export type TSyncPrefix = "CIMS" | "VGG" | "GT" | "TM" | "SG" | "GTX";

type TSyncStatusKey<P extends TSyncPrefix> = `${P}_Sync_Status`;
type TSyncMessageKey<P extends TSyncPrefix> = `${P}_Sync_Message`;

export type TSyncRow<P extends TSyncPrefix = TSyncPrefix> = Partial<Record<TSyncStatusKey<P>, number | string | null>> &
	Partial<Record<TSyncMessageKey<P>, string | null>>;

export type TCollapsedSync<P extends TSyncPrefix> = Record<TSyncStatusKey<P>, number | null> &
	Record<TSyncMessageKey<P>, string | null>;

export const collapseSync = <P extends TSyncPrefix>(rows: Array<TSyncRow<P>>, prefix: P): TCollapsedSync<P> => {
	const statusKey: TSyncStatusKey<P> = `${prefix}_Sync_Status`;
	const messageKey: TSyncMessageKey<P> = `${prefix}_Sync_Message`;
	const status = collapseSyncStatus(rows.map((r) => r[statusKey]));
	const match =
		status === null
			? undefined
			: rows.find((r) => getSyncStatus(r[statusKey])?.id === status && (r[messageKey] ?? "").trim() !== "");
	return { [statusKey]: status, [messageKey]: match?.[messageKey] ?? null } as TCollapsedSync<P>;
};

export const collapseMarketplaceSync = (rows: Array<TSyncRow>): TCollapsedSync<TSyncPrefix> => ({
	...collapseSync(rows, "CIMS"),
	...collapseSync(rows, "VGG"),
	...collapseSync(rows, "GT"),
	...collapseSync(rows, "TM"),
	...collapseSync(rows, "SG"),
	...collapseSync(rows, "GTX"),
});
